/**
 * Сквозной виджет «Финансовое здоровье объекта» (ObjectEconomyCard) —
 * единый финансовый срез для дашбордов Экономиста и Руководителя проекта:
 * цена договора, выполнение КС-2, сертифицированное освоение КС-3,
 * незавершённое производство (НЗП = КС-2 − КС-3), затраты БДР, маржа и аномалии.
 */
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useGetEconObjectEconomyQuery } from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { fmtPercent, fmtPeriod, fmtRub } from '@entities/economic/types';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';

const EconomyWrap = styled(Card)`
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

const MetricCell = styled.div`
  padding: ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const MetricVal = styled.span<{ $tone?: 'danger' | 'warning' | 'success' }>`
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone, theme }) =>
    $tone ? theme.colors.status[$tone] : theme.colors.text.primary};
`;

const MetricSub = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const RunLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/** Пометка «нет данных» по БДР: что не вошло в цифры и почему (ТЗ Э0б, Д6). */
const BdrNote = styled.p`
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12.5px;
`;

const WipAlertBanner = styled.div`
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.status.warning};
  font-size: 12.5px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export function ObjectEconomyCard({
  projectId,
  objectName,
  title,
}: {
  projectId?: string;
  objectName?: string;
  title?: string;
}) {
  const { activeProjectId } = useActiveProjectContext();
  const effectiveProjectId = projectId || activeProjectId || undefined;
  const { data, isLoading, isError } = useGetEconObjectEconomyQuery(
    {
      project_id: effectiveProjectId,
      object_name: objectName || undefined,
    },
    {
      skip: !effectiveProjectId && !objectName,
    },
  );

  if (!effectiveProjectId && !objectName) return null;
  if (isLoading || isError || !data) return null;

  const isWipHigh =
    Boolean(data.wip_alert) ||
    (data.contract_price > 0 && data.wip_amount / data.contract_price > 0.15);
  // Затраты и маржа есть только у утверждённого БДР: без него — «нет данных»
  // текстом, а не ноль затрат и вся выручка в марже.
  const bdrCost = data.bdr_cost_total;
  const margin = data.margin;
  const marginTone =
    margin === null
      ? undefined
      : margin < 0
        ? 'danger'
        : (data.margin_pct ?? 0) >= 10
          ? 'success'
          : undefined;
  const etcRub =
    data.etc_rub ?? Math.max(0, data.contract_price - data.ks3_total);
  const burnRateRub = data.monthly_burn_rate_rub ?? 0;

  return (
    <EconomyWrap>
      <Header>
        <Title>{title ?? `Финансовый паспорт объекта · ${data.object_name}`}</Title>
        <HeaderActions>
          {isWipHigh && (
            <Badge $tone="warning">Повышенный НЗП: требуется закрытие КС-3</Badge>
          )}
          {data.unresolved_critical_anomalies > 0 ? (
            <Badge $tone="danger">
              Критических аномалий: {data.unresolved_critical_anomalies}
            </Badge>
          ) : data.total_anomalies > 0 ? (
            <Badge $tone="warning">Аномалий на контроле: {data.total_anomalies}</Badge>
          ) : (
            <Badge $tone="success">Финансовых блокировок нет</Badge>
          )}
        </HeaderActions>
      </Header>

      {isWipHigh && (
        <WipAlertBanner>
          <span>
            Внимание: объём незапроцентованного выполнения НЗП ({fmtRub(data.wip_amount)}) превышает
            допустимый порог. Необходимо ускорить приёмку ИД/КФО и выпуск справки КС-3.
          </span>
        </WipAlertBanner>
      )}

      <MetricsGrid>
        <MetricCell>
          <MetricLabel>Цена договоров</MetricLabel>
          <MetricVal>{fmtRub(data.contract_price)}</MetricVal>
          <MetricSub>Твёрдая / лимит</MetricSub>
        </MetricCell>

        <MetricCell>
          <MetricLabel>Выполнено КС-2</MetricLabel>
          <MetricVal>{fmtRub(data.ks2_total)}</MetricVal>
          <MetricSub>
            {data.executed_pct != null
              ? `${fmtPercent(data.executed_pct)} от лимита`
              : 'Подтверждённый объём'}
          </MetricSub>
        </MetricCell>

        <MetricCell>
          <MetricLabel>Принято по КС-3</MetricLabel>
          <MetricVal>{fmtRub(data.ks3_total)}</MetricVal>
          <MetricSub>
            {data.certified_pct != null
              ? `${fmtPercent(data.certified_pct)} от лимита`
              : 'Сертифицированный доход'}
          </MetricSub>
        </MetricCell>

        <MetricCell>
          <MetricLabel>НЗП (КС-2 − КС-3)</MetricLabel>
          <MetricVal $tone={isWipHigh ? 'warning' : undefined}>
            {fmtRub(data.wip_amount)}
          </MetricVal>
          <MetricSub>
            {isWipHigh ? 'Требуется закрытие КС-3' : 'Незавершённое производство'}
          </MetricSub>
        </MetricCell>

        <MetricCell>
          <MetricLabel>Остаток до завершения (ETC)</MetricLabel>
          <MetricVal>{fmtRub(etcRub)}</MetricVal>
          <MetricSub>
            {data.estimated_months_to_completion != null
              ? `Прогноз завершения: ~${data.estimated_months_to_completion.toFixed(1)} мес.`
              : 'Остаток лимита по договору'}
          </MetricSub>
        </MetricCell>

        <MetricCell>
          <MetricLabel>Темп освоения / мес.</MetricLabel>
          <MetricVal>{fmtRub(burnRateRub)}</MetricVal>
          <MetricSub>Среднемесячное выполнение</MetricSub>
        </MetricCell>

        <MetricCell>
          <MetricLabel>Затраты БДР</MetricLabel>
          <MetricVal>{bdrCost === null ? 'Нет данных' : fmtRub(bdrCost)}</MetricVal>
          <MetricSub>
            {bdrCost === null ? 'Нет утверждённого БДР' : 'По утверждённому БДР'}
          </MetricSub>
        </MetricCell>

        <MetricCell>
          <MetricLabel>Маржинальность</MetricLabel>
          <MetricVal $tone={marginTone}>{margin === null ? 'Нет данных' : fmtRub(margin)}</MetricVal>
          <MetricSub>
            {margin === null
              ? 'Нет затрат утверждённого БДР'
              : `Рентабельность: ${fmtPercent(data.margin_pct)}`}
          </MetricSub>
        </MetricCell>
      </MetricsGrid>

      {data.bdr_note && <BdrNote role="status">{data.bdr_note}</BdrNote>}

      <Footer>
        <span>
          {data.latest_period
            ? `Последний расчёт БДР за ${fmtPeriod(data.latest_period)}`
            : 'Расчёты БДР по объекту ещё не проводились'}
        </span>
        {data.latest_run_id && (
          <RunLink to={`/agents/econ/runs/${data.latest_run_id}`}>
            Открыть карточку БДР в Экономисте →
          </RunLink>
        )}
      </Footer>
    </EconomyWrap>
  );
}
